import { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";

import Mask from "./components/Mask";
import NavBar from "./components/NavBar";
import HeroSection from "./components/HeroSection";
import Loading from "./components/Loading";
// import Credits from "./components/Credits";
import UnderConstructionPage from "./components/UnderConstructionPage";
import Portfolio from "./components/Selector";
import NotFound from "./components/NotFound";
// ── GAME INTEGRATION ──
import { GameProvider } from "./context/GameContext";
// import FieldTray from "./components/FieldTray";

const Experience = lazy(() => import("./components/Projects"));
const Gallery = lazy(() => import("./components/Gallery"));
const Techstack = lazy(() => import("./components/Techstack"));
const About = lazy(() => import("./components/About"));
const LetsTalk = lazy(() => import("./components/LetsTalk"));
// Game routes — lazy loaded
// const Game    = lazy(() => import("./components/Game/Game"));
// const DataBag = lazy(() => import("./components/DataBag"));

/* ---------- Entry Gate ---------- */
function EntryGate({ ready, entered, setEntered, menuOpen, setMenuOpen, children }) {
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    if (menuOpen) {
      setMenuOpen(false);
    }
  }, [location.pathname]);

  if (!entered) {
    return (
      <Loading
        ready={ready}
        redirectTo={location.pathname === "/" ? "/home" : location.pathname}
        onEnter={() => setEntered(true)}
      />
    );
  }

  return (
    <>
      {children}
      {/* GAME INTEGRATION: FieldTray persists across all routes */}
      {/* <FieldTray /> */}
    </>
  );
}

function App() {
  const [showMask, setShowMask] = useState(true);
  const [ready, setReady] = useState(false);
  const [entered, setEntered] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLargeScreenSupported, setIsLargeScreenSupported] = useState(true);
  const [allowUnsupportedScreen, setAllowUnsupportedScreen] = useState(false);

  useEffect(() => {
    const checkDeviceSupport = () => {
      const userAgent = navigator.userAgent || "";
      const isTabletOrMobileUA =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|tablet/i.test(userAgent);
      const hasCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;
      const hasTouchInput = navigator.maxTouchPoints > 0;
      const isSmallViewport = window.innerWidth < 1200 || window.innerHeight < 700;

      const shouldBlock = isTabletOrMobileUA || ((hasCoarsePointer || hasTouchInput) && isSmallViewport);
      setIsLargeScreenSupported(!shouldBlock);
    };

    checkDeviceSupport();
    window.addEventListener("resize", checkDeviceSupport);
    window.addEventListener("orientationchange", checkDeviceSupport);

    return () => {
      window.removeEventListener("resize", checkDeviceSupport);
      window.removeEventListener("orientationchange", checkDeviceSupport);
    };
  }, []);

  /* Mask */
  useEffect(() => {
    const t = setTimeout(() => setShowMask(false), 900);
    return () => clearTimeout(t);
  }, []);

  /* Preload */
  useEffect(() => {
    Promise.all([
      document.fonts?.ready,
      import("./components/About"),
      import("./components/Gallery"),
      import("./components/Projects"),
      import("./components/Techstack"),
      import("./components/LetsTalk"),
      // import("./components/Game/Game"),   // preload game
      // import("./components/DataBag"),     // preload databag
    ]).then(() => setReady(true));
  }, []);

  return (
    // GAME INTEGRATION: wrap entire app in GameProvider
    // <GameProvider>
      <Router>
        <div className="App">
          {!isLargeScreenSupported && !allowUnsupportedScreen && (
            <Loading
              isBlocked
              blockTitle="Large Screen Required"
              blockMessage="You are viewing on a device that does not meet the recommended screen size for this portfolio. The experience would be limited."
              blockHint="Recommended resolution: 1920 x 1080 or higher."
              onStillContinue={() => setAllowUnsupportedScreen(true)}
            />
          )}

          {(isLargeScreenSupported || allowUnsupportedScreen) && showMask && <Mask />}

          {(isLargeScreenSupported || allowUnsupportedScreen) && !showMask && (
            <EntryGate
              ready={ready}
              entered={entered}
              setEntered={setEntered}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
            >
              <NavBar
                audioEnabled={audioEnabled}
                setAudioEnabled={setAudioEnabled}
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
              />

              <Suspense fallback={<Loading ready={false} />}>
                <Routes>
                  {/* Home */}
                  <Route
                    path="/home"
                    element={<HeroSection audioEnabled={audioEnabled} />}
                  />

                  {/* Redirect root */}
                  <Route path="/" element={<Navigate to="/home" replace />} />

                  <Route path="/experience" element={<Experience />} />
                  <Route path="/projects" element={<Gallery />} />
                  <Route path="/tools" element={<Techstack />} />
                  <Route path="/about" element={<About />} />
                  {/* <Route path="/credits" element={<Credits />} /> */}
                  <Route path="/testimonials" element={<UnderConstructionPage audioEnabled={audioEnabled} onOpenMenu={() => setMenuOpen(true)} />} />
                  <Route path="/blogs" element={<UnderConstructionPage audioEnabled={audioEnabled} onOpenMenu={() => setMenuOpen(true)} />} />
                  <Route path="/contact" element={<LetsTalk />} />
                  <Route path="/portfolio" element={<Portfolio audioEnabled={audioEnabled} onOpenMenu={() => setMenuOpen(true)} />} />
                  {/* GAME INTEGRATION: new routes 
                  <Route path="/game"    element={<Game />} />
                  <Route path="/databag" element={<DataBag />} />*/}
                  <Route path="*" element={<NotFound/>} />
                </Routes>
              </Suspense>
            </EntryGate>
          )}
        </div>
      </Router>
    // </GameProvider>
  );
}

export default App;
